import { Component, ViewEncapsulation, OnInit, OnDestroy, AfterViewInit, ViewChild, HostListener } from '@angular/core';
import * as GC from '@grapecity/spread-sheets';
import { mapActionKeys } from '@helpers/spread/map-action-keys';
import { HoverNotification } from '@models/hover-notification';
import { UnusedAccount } from '@models/unused-account';
import { WindowRef } from '@progress/kendo-angular-dialog';
import { PermissionsService } from '@services/auth/permissions.service';
import { UnusedAccountsService } from '@services/balance-sheet/unused-accounts.service';
import { MenuService } from '@services/menu/menu.service';
import { NotificationsService } from '@services/notifications/notifications.service';
import {ToastLevel} from '@shared/toasterSetup';
import { setDefaultBookOptions, setDefaultSheetOptions } from '@shared/spreadsheet/base';
import { SpreadContextMenu } from '@shared/spreadsheet/protection';
import { spreadsheetConfig } from '@shared/spreadsheet/spreadsheet-config';
import { resumeAll } from '@helpers/spread/resume-all';
import { suspendAll } from '@helpers/spread/suspend-all';
import { Toast } from 'ngx-toastr';
import { fromEvent, Subject } from 'rxjs';
import { takeUntil } from 'rxjs/operators';
import Swal, { SweetAlertOptions } from 'sweetalert2';
import * as SheetFunctions from '@helpers/spread/sheet-functions';
import { clipboardClearOnCut } from '@helpers/clipboard-events';

const MIN_ROW_COUNT = 200;
const EMPTY_BOTTOM_ROWS = 2;
const PROCESSING_ROWS_THRESHOLD = 100;
const COL_ACCOUNT_NAME = 0;
const COL_ACCOUNT_MNEMONIC = 1;
const COL_ACCOUNT_NUMBER = 2;

@Component({
  encapsulation: ViewEncapsulation.None,
  selector: 'app-unused-accounts',
  templateUrl: './unused-accounts.component.html',
  styleUrls: ['./unused-accounts.component.scss'],
})
export class UnusedAccountsComponent implements OnInit, OnDestroy, AfterViewInit {
  @ViewChild('unusedAccSpreadContainer', { static: false }) spreadContainer;
  @ViewChild('unusedAccSpreadDiv', { static: false }) spreadDiv;

  public spreadBackColor = spreadsheetConfig.backColor;
  public hostStyle = spreadsheetConfig.hostStyle;  
  public spread: GC.Spread.Sheets.Workbook;
  public sheet: GC.Spread.Sheets.Worksheet;
  public isSaving = false;
  public isLoading = true;
  public isProcessing = false;
  public userRoleCanEdit: boolean;
  public notification: HoverNotification;
  private hoverNotificationSub;
  private destroy$ = new Subject<void>();
  private resizeObserver: ResizeObserver;
  private lastPasteInfo: { text: string; startRow: number; startCol: number; timestamp: number } | null = null;
  //public readonly spreadConfig = SpreadConfig;
  //private data: UnusedAccount[] = [{AccountName:'Test',AccountNumber:1234,Mnemonic:'TST'}];

  get showProcessing(): boolean {
    return this.isProcessing && this.sheet && this.sheet.getRowCount() > PROCESSING_ROWS_THRESHOLD;
  }

  @HostListener('keydown', ['$event'])
  public keydown(event: any): void {
    if (event.key === 'Escape') {
      event.preventDefault();
      this.onEscapePressed(event);
    } else if (event.ctrlKey && event.key === 'a') {
      event.preventDefault();
      this.selectAll();
    }
  }

  constructor(
    public windowRef: WindowRef,
    private unusedAccountsService: UnusedAccountsService,
    private notifications: NotificationsService,
    private permissions: PermissionsService,
    private menu: MenuService
  ) {
    this.userRoleCanEdit = this.permissions.getPermission('ModelStructureBalanceSheet') === 'W';
  }

  ngOnInit() {
    // Add window resize listener
    fromEvent(window, 'resize')
      .pipe(takeUntil(this.destroy$))
      .subscribe(() => {
        this.handleWindowResize();
      });
  }

  ngOnDestroy() {
    this.menu.unusedAccountsWindowOpen$.next(false);
    this.hoverNotificationSub.unsubscribe();
    this.destroy$.next();
    this.destroy$.complete();
    if (this.resizeObserver) {
      this.resizeObserver.disconnect();
    }
  }

  ngAfterViewInit() {
    this.hoverNotificationSub = fromEvent(this.spreadContainer.nativeElement, 'mousemove').subscribe((e: any) => {
      this.notification = this.onMouseMove(this.sheet, e.layerX, e.layerY);
    });

    // Add ResizeObserver to watch for size changes
    this.resizeObserver = new ResizeObserver(() => {
      this.handleWindowResize();
    });
    if (this.spreadContainer && this.spreadContainer.nativeElement) {
      this.resizeObserver.observe(this.spreadContainer.nativeElement);
      
      // Add click handler to ensure spreadsheet gets focus
      this.spreadContainer.nativeElement.addEventListener('click', () => {
        this.spreadContainer.nativeElement.focus();
        
        // Also try to focus the SpreadJS element directly
        if (this.spread && this.spread.getHost()) {
          this.spread.getHost().focus();
        }
      });
    }

    // Add global paste detection for early intervention
    fromEvent(document, 'paste')
      .pipe(takeUntil(this.destroy$))
      .subscribe((event: ClipboardEvent) => {
        // Only handle paste events when the spreadsheet has focus
        const spreadsheetContainer = document.getElementById(spreadsheetConfig.containerParentId);
        const activeElement = document.activeElement;
        const hasFocus = document.activeElement.closest(`#${spreadsheetConfig.containerParentId}`) || 
                        (spreadsheetContainer && spreadsheetContainer.contains(activeElement)) ||
                        (this.spread && this.spread.getActiveSheet() && activeElement === this.spreadContainer?.nativeElement) ||
                        (activeElement && activeElement.tagName === 'CANVAS') ||
                        (activeElement && activeElement.classList.contains('gc-spread-sheets'));
        
        if (this.sheet && this.spread && hasFocus) {
          try {
            const clipboardData = event.clipboardData;
            if (clipboardData && clipboardData.getData) {
              const text = clipboardData.getData('text');
              if (text) {
                const lines = text.trim().split('\n');
                
                if (lines.length > 0) {
                  // Calculate max columns in clipboard data
                  const columnCounts = lines.map(line => line.split('\t').length);
                  const maxColumns = Math.max(...columnCounts);
                  const startCol = this.sheet.getActiveColumnIndex();
                  
                  // If paste would exceed available columns, prevent it
                  if (startCol + maxColumns > 3) {
                    event.preventDefault();
                    event.stopPropagation();
                    
                    // Show notification to user
                    this.notifications.showToast(
                      ToastLevel.Warning, 
                      { message: 'The data you are trying to paste contains more columns than are available in the sheet.' } as Toast
                    );
                    return false;
                  }
                  
                  // Check if we need row expansion
                  const startRow = this.sheet.getActiveRowIndex();
                  const currentRowCount = this.sheet.getRowCount();
                  const requiredRows = startRow + lines.length;
                  
                  if (requiredRows > currentRowCount) {
                    event.preventDefault();
                    event.stopPropagation();
                    
                    // Handle the paste manually
                    this.handleCustomPaste(text, startRow, startCol);
                    return false;
                  }
                }
              }
            }
          } catch (error) {
            console.error('Error in document paste handler:', error);
          }
        } else {
          // Always handle paste manually when detected, regardless of focus
          try {
            const clipboardData = event.clipboardData;
            if (clipboardData && clipboardData.getData) {
              const text = clipboardData.getData('text');
              if (text && this.sheet) {
                // Prevent the default paste behavior
                event.preventDefault();
                event.stopPropagation();
                
                // Get the current selection or use a default
                let startRow = 0;
                let startCol = 0;
                
                try {
                  const selection = this.sheet.getSelections()[0];
                  if (selection) {
                    startRow = selection.row >= 0 ? selection.row : 0;
                    startCol = selection.col >= 0 ? selection.col : 0;
                  } else {
                    // If no selection, use the active cell
                    startRow = this.sheet.getActiveRowIndex();
                    startCol = this.sheet.getActiveColumnIndex();
                  }
                } catch (selectionError) {
                  startRow = 0;
                  startCol = 0;
                }
                
                this.handleCustomPaste(text, startRow, startCol);
              }
            }
          } catch (error) {
            console.error('Error in manual paste handling:', error);
          }
        }
      });

    // Enhanced clipboard paste detection - Use keydown for early detection
    fromEvent(document, 'keydown')
      .pipe(takeUntil(this.destroy$))
      .subscribe((event: KeyboardEvent) => {
        const isPasteCommand = event && ['KeyV', 'v'].includes(event.code) && event.ctrlKey;
        if (isPasteCommand && this.sheet && document.activeElement.closest(`#${spreadsheetConfig.containerParentId}`)) {
          try {
            // Get current selection
            const selection = this.sheet.getSelections()[0];
            if (selection) {
              const range = {
                row: selection.row >= 0 ? selection.row : 0,
                col: selection.col >= 0 ? selection.col : 0,
                rowCount: selection.rowCount,
                colCount: selection.colCount,
              };
              
              // Use a slightly longer timeout to ensure the paste has fully completed
              setTimeout(() => {
                this.clipboardPastedCallback(range);
                this.isValidSheet();
              }, 50);
            }
          } catch (error) {
            console.error('Error handling paste shortcut:', error);
          }
        }
      });
  }

  workbookInit(args) {
    this.spread = args.spread;
    this.hostStyle = spreadsheetConfig.hostStyle;  
    this.setupWorkBook();
  }

  async closeWindow($event?) {
    if ($event) {
      $event.preventDefault();
      $event.stopPropagation();
    }
    if (this.userRoleCanEdit && this.sheet.hasPendingChanges()) {
      const text = 'You have unsaved changes on this page. Please click Cancel to return to the page or Continue to discard changes.';
      const title = 'Unsaved Changes!';
      const confirmButtonText = 'Continue';
      const cancelButtonText = 'Cancel';
      const confirmButtonColor = '#A9A9A9';
      const cancelButtonColor = '#3085d6';

      await Swal.fire({
        title: title,
        text: text,
        type: 'warning',
        showCancelButton: true,
        confirmButtonText,
        cancelButtonText,
        reverseButtons: true,
        allowEscapeKey: true,
        confirmButtonColor, // If =null, use default color
        cancelButtonColor, // If =null, use default color
      } as SweetAlertOptions).then(async result => {
        if (result.value === true) {
          this.windowRef.close();
        }
      });
    } else {
      this.windowRef.close();
    }
  }

  public onEscapePressed(event) {
    event.preventDefault();
    this.closeWindow(event);
  }

  public selectAll() {
    if (this.sheet) {
      // Select all cells in the sheet
      this.sheet.setSelection(0, 0, this.sheet.getRowCount(), this.sheet.getColumnCount());
    }
  }
  saveModel() {
    if (!this.userRoleCanEdit) {
      return;
    }
    
    this.isSaving = true;
    const data = this.sheet.getDataSource();
    
    // Convert string account numbers back to numbers for the API
    const formattedData = data.map(account => ({
      ...account,
      AccountNumber: account.AccountNumber ? parseInt(account.AccountNumber, 10) : null
    })).filter(acc => acc.AccountNumber !== null);

    this.unusedAccountsService
      .saveUnusedAccounts(formattedData)
      .then(x => {
        this.unusedAccountsService.fetchUnusedAccounts();
        const defaultToastMessage: Toast = {          
          title: 'Unused Accounts Saved',
          message: 'Unused account list has been saved successfully.',
        } as Toast;
        this.notifications.showToast(ToastLevel.Success, defaultToastMessage);
        this.sheet.clearPendingChanges();
        this.closeWindow();
      })
      .catch(x => {
        // Show the toast
        const defaultToastMessage: Toast = {          
          title: 'Error saving accounts',
          message: 'The service was unable to save the list of accounts.',
        } as Toast;
        this.notifications.showToast(ToastLevel.Error, defaultToastMessage);
      })
      .finally(() => {
        this.isSaving = false;
      });
  }

  public isSaveEnabled() {
    try {
      return this.userRoleCanEdit && 
             !this.isLoading && 
             this.sheet && 
             this.sheet.hasPendingChanges() && 
             this.isValidSheet();
    } catch (error) {
      return false;
    }
  }

  public isValidSheet() {
    if (!this.sheet) return false;
    
    const dataSource = this.sheet.getDataSource();
    if (!dataSource) return false;

    // Create a Set to track unique account numbers
    const accountNumbers = new Set<string>();

    // Validate only rows with data
    for (const row of dataSource) {
      if (!row || (!row.AccountName && !row.Mnemonic && !row.AccountNumber)) {
        continue;
      }

      const accNum = row.AccountNumber?.toString();
      
      // If any field has data, account number is required
      if (!accNum && (row.AccountName || row.Mnemonic)) {
        return false;
      }
      
      // Skip empty rows
      if (!accNum) continue;

      // Check for duplicates
      if (accountNumbers.has(accNum)) {
        return false;
      }
      accountNumbers.add(accNum);
    }

    return true;
  }

  setupWorkBook() {
    this.sheet = this.spread.getActiveSheet();    
    this.sheet.name('Unused Accounts');
    this.sheet.defaults.rowHeight = 20;
    this.sheet.defaults.colWidth = 100;
    this.sheet.setColumnCount(3);

    // Ensure the spreadsheet gets focus
    setTimeout(() => {
      if (this.spreadContainer && this.spreadContainer.nativeElement) {
        this.spreadContainer.nativeElement.focus();
      }
    }, 100);

    // Add workbook-level edit prevention - this is the main protection mechanism
    this.spread.bind(GC.Spread.Sheets.Events.EditStarting, (sender, args) => {
      if (!this.userRoleCanEdit) {
        args.cancel = true;
      }
    });

    if (!this.userRoleCanEdit) {
      // Lock the sheet
      this.sheet.options.isProtected = true;
      
      // Lock all cells
      const range = this.sheet.getRange(0, 0, this.sheet.getRowCount(), this.sheet.getColumnCount());
      range.locked(true);
      range.backColor('#f5f5f5');

      // Disable all interactions
      this.sheet.options.protectionOptions = {
        allowSelectLockedCells: false,
        allowSelectUnlockedCells: false,
        allowSort: false,
        allowFilter: false,
        allowResizeRows: false,
        allowResizeColumns: false,
        allowEditObjects: false,
        allowDragInsertRows: false,
        allowDragInsertColumns: false,
        allowInsertRows: false,
        allowInsertColumns: false,
        allowDeleteRows: false,
        allowDeleteColumns: false
      };
    } else {
      // Edit mode setup - configure for paste range extension
      this.spread.options.allowContextMenu = true;
      this.spread.options.allowExtendPasteRange = true;
      this.spread.options.allowUserResize = true;
      
      // Configure sheet protection options for paste range extension
      this.sheet.options.protectionOptions = {
        allowSelectLockedCells: true,
        allowSelectUnlockedCells: true,
        allowSort: false,
        allowFilter: false,
        allowResizeRows: true,
        allowResizeColumns: false, // Keep columns fixed
        allowEditObjects: false,
        allowDragInsertRows: true,
        allowDragInsertColumns: false, // Keep columns fixed
        allowInsertRows: true,
        allowInsertColumns: false, // Keep columns fixed
        allowDeleteRows: true,
        allowDeleteColumns: false // Keep columns fixed
      };
      
      // Handle edits efficiently
      this.sheet.bind(GC.Spread.Sheets.Events.ValueChanged, () => {
        // Trigger validation and row addition in the next tick
        setTimeout(() => this.onChanges(), 0);
      });

      // Add clipboard event handlers for paste operations
      this.setupClipboardEvents();
    }

    setDefaultBookOptions(this.spread.options, { showHorizontalScrollbar: false });
    setDefaultSheetOptions(this.sheet.options);

    // Load data
    this.loadData();
  }

  private ensureMinimumStructure() {
    if (!this.sheet) return;
    
    let structureChanged = false;
    
    // Always ensure we have the minimum structure
    const currentRowCount = this.sheet.getRowCount();
    if (currentRowCount < MIN_ROW_COUNT) {
      this.sheet.setRowCount(MIN_ROW_COUNT);
      structureChanged = true;
    }
    
    // Ensure columns are set up
    if (this.sheet.getColumnCount() < 3) {
      this.sheet.setColumnCount(3);
      structureChanged = true;
    }
    
    // Note: Structure changes are now handled in initial setup to prevent false change detection
    
    // Force a refresh to ensure the structure is visible
    setTimeout(() => {
      if (this.spread) {
        this.spread.refresh();
        this.spread.repaint();
      }
    }, 0);
  }

  private async loadData() {
    try {
      const accs = await this.unusedAccountsService.fetchUnusedAccounts();
      this.setupDatabinding(accs);
      this.applyProtection();
      this.ensureMinimumStructure(); // Ensure structure is maintained
      
      this.isLoading = false;
    } catch (error) {
      console.error('Error loading unused accounts:', error);
      this.ensureMinimumStructure(); // Ensure structure even on error
      
      this.isLoading = false;
    }
  }

  private applyProtection() {
    // Always ensure we have at least MIN_ROW_COUNT rows before applying protection
    const currentRowCount = this.sheet.getRowCount();
    if (currentRowCount < MIN_ROW_COUNT) {
      this.sheet.setRowCount(MIN_ROW_COUNT);
    }
    
    const range = this.sheet.getRange(0, 0, this.sheet.getRowCount(), this.sheet.getColumnCount());
    
    if (this.userRoleCanEdit) {
      range.locked(false);
      SpreadContextMenu.setCopyCutPaste(this.spread, 'spread-container--unused-accounts').enableDeleteRows(this.spread);
      this.addDataValidation();
    } else {
      // Lock all cells for read-only mode
      range.locked(true);
      range.backColor('#f5f5f5'); // Light gray background to indicate read-only
      this.sheet.options.isProtected = true;
      
      // Force refresh to ensure protection is applied
      this.spread.refresh();
    }
  }

  private onChanges() {
    if (!this.isLoading && this.userRoleCanEdit) {
      // Only suspend events for the heavy operations
      this.addDataValidation();
      
      // Always ensure minimum row count is maintained
      const currentRowCount = this.sheet.getRowCount();
      const dataLength = this.sheet.getDataSource()?.length || 0;
      
      // Ensure we always have at least MIN_ROW_COUNT rows
      if (currentRowCount < MIN_ROW_COUNT) {
        suspendAll(this.sheet);
        try {
          this.sheet.addRows(currentRowCount, MIN_ROW_COUNT - currentRowCount);
        } finally {
          resumeAll(this.sheet);
        }
      }
      // Check if we need to add more rows for data entry
      else if (currentRowCount <= dataLength) {
        const lastRow = dataLength - 1;
        const secondLastRow = dataLength - 2;
        
        if (this.hasData(lastRow) || this.hasData(secondLastRow)) {
          suspendAll(this.sheet);
          try {
            this.sheet.addRows(currentRowCount, 2);
          } finally {
            resumeAll(this.sheet);
          }
        }
      }

      // Note: Empty rows are now included in initial data binding to prevent false change detection
    }
  }

  private hasData(rowIndex: number): boolean {
    if (rowIndex < 0) return false;
    
    const row = this.sheet.getDataSource()[rowIndex];
    return row && (row.AccountName || row.Mnemonic || row.AccountNumber);
  }

  private addDataValidation() {
    if (!this.userRoleCanEdit) return;
    
    const dataSource = this.sheet.getDataSource();
    if (!dataSource || !dataSource.length) return;

    // Create a Set of all account numbers for faster lookup
    const accountNumbers = new Set<string>();
    let hasInvalidNumbers = false;

    // Single pass through data to check for duplicates
    for (const row of dataSource) {
      if (!row?.AccountNumber) continue;
      
      const accNum = row.AccountNumber.toString();
      if (accountNumbers.has(accNum)) {
        hasInvalidNumbers = true;
        break; // Exit early once we find a duplicate
      }
      accountNumbers.add(accNum);
    }

    if (hasInvalidNumbers) {
      suspendAll(this.sheet);
      try {
        const dv = GC.Spread.Sheets.DataValidation.createFormulaValidator(
          `AND(COUNTIF(C:C, C1) <= 1, LEN(C1) > 0)`
        );
        dv.ignoreBlank(true);
        dv.errorMessage('Account number must be unique and non-empty');
        
        // Apply validation to the entire column at once
        this.sheet.setDataValidator(0, COL_ACCOUNT_NUMBER, this.sheet.getRowCount(), 1, dv);
      } finally {
        resumeAll(this.sheet);
      }
    }
  }

  private setupDatabinding(dataSource: UnusedAccount[]) {
    if (!this.spread || !this.sheet) return;

    suspendAll(this.sheet);      
    
    try {
      // Always bind columns first
      this.sheet.bindColumns([
        { name: 'AccountName', displayName: 'Account Name', size: 300 },
        { name: 'Mnemonic', displayName: 'Mnemonic', size: 150 },
        { name: 'AccountNumber', displayName: 'Account Numbers', size: 120, formatter: '@' }
      ]);

      // If no data, create blank rows with correct keys
      let formattedDataSource: any[] = [];
      if (dataSource && dataSource.length > 0) {
        formattedDataSource = dataSource.map(account => ({
          AccountName: account.AccountName || '',
          Mnemonic: account.Mnemonic || '',
          AccountNumber: account.AccountNumber ? account.AccountNumber.toString() : ''
        }));
      }
      
      // Always ensure we have at least MIN_ROW_COUNT rows plus empty bottom rows
      const totalRowsNeeded = Math.max(MIN_ROW_COUNT, formattedDataSource.length + EMPTY_BOTTOM_ROWS);
      
      // Add empty rows to reach the required count
      while (formattedDataSource.length < totalRowsNeeded) {
        formattedDataSource.push({ AccountName: '', Mnemonic: '', AccountNumber: '' });
      }

      this.sheet.setRowCount(formattedDataSource.length);
      this.sheet.setDataSource(formattedDataSource);

      // Apply cell-level protection if needed
      if (!this.userRoleCanEdit) {
        const range = this.sheet.getRange(0, 0, formattedDataSource.length, 3);
        range.locked(true);
        range.backColor('#f5f5f5');
      }

      this.sheet.clearSelection();

      // Clear pending changes AFTER all structural setup is complete
      this.sheet.clearPendingChanges();
    } finally {
      resumeAll(this.sheet);
      
      // Final refresh to ensure structure is visible
      setTimeout(() => {
        this.spread.refresh();
        this.spread.repaint();
      }, 0);
    }
  }

  private onMouseMove = (sheet: GC.Spread.Sheets.Worksheet, layerX: number, layerY: number): HoverNotification => {
    const target = sheet.hitTest(layerX, layerY);
    if (!target) return {};

    const { row, col } = target;
    const thisCell = sheet.getCell(row, col);
    const value = thisCell.value();
    const validator = thisCell.validator();
    const isValid = sheet.isValid(row, col, value);

    if (!validator || isValid || row < 0 || col < 0 || col !== COL_ACCOUNT_NUMBER) return {};

    return {
      title: 'Invalid Account Number',
      value: value,
      body: 'The Account Number should be an unique number >= 0',
      type: 'error',
    };
  };

  private handleWindowResize() {
    if (this.spread && this.sheet) {
      // Force the spreadsheet to recalculate its size
      setTimeout(() => {
        this.spread.refresh();
        this.spread.repaint();
      }, 100);
    }
  }

  private setupClipboardEvents() {
    if (!this.userRoleCanEdit) return;

    this.sheet.bind(GC.Spread.Sheets.Events.ClipboardPasting, (e, info) => {
      try {
        // If this is a cut event, clear clipboard content once event ends
        clipboardClearOnCut(info, this.sheet);
        
        // Validate paste data before proceeding
        if (!info) {
          return;
        }

        // Calculate rows needed from clipboard data
        let rowCount = 1;
        let clipboardText = '';
        
        if (info.pasteData && info.pasteData.text) {
          clipboardText = info.pasteData.text;
          const lines = clipboardText.trim().split('\n');
          rowCount = lines.length;
          
          // Get the target paste location
          const startRow = this.sheet.getActiveRowIndex();
          const startCol = this.sheet.getActiveColumnIndex();
          const currentRowCount = this.sheet.getRowCount();
          const requiredRows = startRow + rowCount;
          
          // Check if this would exceed available columns
          const columnCounts = lines.map(line => line.split('\t').length);
          const maxColumns = Math.max(...columnCounts);
          
          if (startCol + maxColumns > 3) {
            e.preventDefault();
            e.stopPropagation();
            
            this.notifications.showToast(
              ToastLevel.Warning, 
              { message: 'The data you are trying to paste contains more columns than are available in the sheet.' } as Toast
            );
            return;
          }
          
          // If we need more rows, handle the paste manually
          if (requiredRows > currentRowCount) {
            // Prevent the default paste behavior
            e.preventDefault();
            e.stopPropagation();
            
            // Handle the paste manually
            this.handleCustomPaste(clipboardText, startRow, startCol);
            return;
          }
        } else if (info.cellRange && info.cellRange.rowCount) {
          rowCount = info.cellRange.rowCount;
        }

        // Check if we're trying to paste a large amount of data
        if (rowCount > PROCESSING_ROWS_THRESHOLD) {
          this.isProcessing = true;
        }
        
        // For smaller pastes that don't need row expansion, let SpreadJS handle it
        
      } catch (error) {
        console.error('Error handling clipboard pasting event:', error);
        // Don't prevent default paste behavior even if our handler fails
      }
    });

    this.sheet.bind(GC.Spread.Sheets.Events.ClipboardPasted, (e, info) => {
      try {
        if (info && info.cellRange) {
          this.clipboardPastedCallback(info.cellRange);
        }
      } catch (error) {
        console.error('Error in ClipboardPasted event handler:', error);
        // Attempt recovery by checking for active selection
        try {
          const selection = this.sheet.getSelections()[0];
          if (selection) {
            this.clipboardPastedCallback({
              row: selection.row >= 0 ? selection.row : 0,
              col: selection.col >= 0 ? selection.col : 0,
              rowCount: selection.rowCount,
              colCount: selection.colCount
            });
          }
        } catch (recoveryError) {
          console.error('Failed to recover from ClipboardPasted error:', recoveryError);
        }
      }
    });

    // DragFillBlockCompleted
    this.sheet.bind(GC.Spread.Sheets.Events.DragFillBlockCompleted, (e, info) => {
      this.clipboardPastedCallback(info.fillRange);
    });
  }

  private clipboardPastedCallback(range) {
    if (!range) {
      return;
    }

    if (this.userRoleCanEdit) {
      try {
        // Validate range parameters
        if (range.row < 0) range.row = 0;
        if (range.col < 0) range.col = 0;
        if (!range.rowCount || range.rowCount < 1) range.rowCount = 1;
        if (!range.colCount || range.colCount < 1) range.colCount = 1;
        
        // Check if we need to add more rows to accommodate paste
        const currentRowCount = this.sheet.getRowCount();
        const requiredRowCount = range.row + range.rowCount;
        
        if (requiredRowCount > currentRowCount) {
          this.sheet.addRows(currentRowCount, requiredRowCount - currentRowCount);
          
          // Synchronize data source after adding rows
          this.syncDataSourceWithSheet();
        }
        
        // Update data validation after paste
        this.addDataValidation();

        // For large paste operations, we may need separate handling
        const isLargePaste = range.rowCount > PROCESSING_ROWS_THRESHOLD;

        // Schedule post-paste processing to run after the paste operation completes
        setTimeout(() => {
          try {
            this.checkIfDataChanged();
            this.validateAndStore();
            
            // Note: Empty rows are now included in initial data binding to prevent false change detection
            
            // Synchronize data source one more time to ensure consistency
            this.syncDataSourceWithSheet();
            
            // Make sure we can see the pasted data by scrolling to it if needed
            if (range.rowCount > 1) {
              // Scroll to make the last row of pasted data visible
              const lastPastedRow = range.row + range.rowCount - 1;
              this.sheet.showRow(lastPastedRow, GC.Spread.Sheets.VerticalPosition.center);
            }
          } catch (error) {
            console.error('Error in post-paste processing:', error);
          } finally {
            // Always reset processing flag after paste operations complete
            this.isProcessing = false;
          }
        }, isLargePaste ? 300 : 100); // Use longer delay for large pastes
      } catch (error) {
        console.error('Error in clipboardPastedCallback:', error);
        this.isProcessing = false;
      }
    }
  }

  private checkIfDataChanged() {
    // This method can be called to check if data has changed and trigger validation
    if (this.sheet && this.userRoleCanEdit) {
      this.onChanges();
    }
  }

  private validateAndStore() {
    // This method can be called to validate and store data changes
    if (this.sheet && this.userRoleCanEdit) {
      this.addDataValidation();
    }
  }

  private setBottomEmptyRows(source: string = null) {
    this.addBottomEmptyRows(EMPTY_BOTTOM_ROWS);
  }

  private addBottomEmptyRows(rows: number) {
    if (!this.sheet || !this.userRoleCanEdit) return;

    try {
      const currentRowCount = this.sheet.getRowCount();
      const dataSource = this.sheet.getDataSource();
      const dataLength = dataSource ? dataSource.length : 0;
      
      // Calculate how many empty rows we need at the bottom
      const emptyRowsNeeded = Math.max(rows, MIN_ROW_COUNT - currentRowCount);
      
      if (emptyRowsNeeded > 0) {
        this.sheet.addRows(currentRowCount, emptyRowsNeeded);
        
        // Synchronize data source after adding rows
        this.syncDataSourceWithSheet();
      }
      
      // Ensure we always have at least MIN_ROW_COUNT rows
      if (this.sheet.getRowCount() < MIN_ROW_COUNT) {
        const additionalRows = MIN_ROW_COUNT - this.sheet.getRowCount();
        this.sheet.addRows(this.sheet.getRowCount(), additionalRows);
        
        // Synchronize data source again if needed
        this.syncDataSourceWithSheet();
      }
    } catch (error) {
      console.error('Error adding bottom empty rows:', error);
    }
  }

  private syncDataSourceWithSheet() {
    if (!this.sheet) return;

    try {
      const rowCount = this.sheet.getRowCount();
      const dataSource = this.sheet.getDataSource() || [];
      
      // Remove excess rows from data source
      if (dataSource.length > rowCount) {
        dataSource.splice(rowCount);
      }
      
      // Add missing rows to data source
      while (dataSource.length < rowCount) {
        dataSource.push({ AccountName: '', Mnemonic: '', AccountNumber: '' });
      }
      
      // Only update if there's a difference to avoid unnecessary operations
      if (dataSource.length !== this.sheet.getDataSource()?.length) {
        this.sheet.setDataSource(dataSource);
      }
    } catch (error) {
      console.error('Error synchronizing data source with sheet:', error);
    }
  }

  private handleCustomPaste(clipboardData: string, startRow: number, startCol: number) {
    if (!this.sheet || !this.userRoleCanEdit) return;

    // Ensure the spreadsheet has focus before pasting
    if (this.spreadContainer && this.spreadContainer.nativeElement) {
      this.spreadContainer.nativeElement.focus();
    }

    try {
      const lines = clipboardData.trim().split('\n');
      const rowCount = lines.length;
      
      // Set processing flag for large operations
      if (rowCount > PROCESSING_ROWS_THRESHOLD) {
        this.isProcessing = true;
      }
      
      // Ensure we have enough rows
      const currentRowCount = this.sheet.getRowCount();
      if (startRow + rowCount > currentRowCount) {
        const additionalRows = startRow + rowCount - currentRowCount + EMPTY_BOTTOM_ROWS;
        
        suspendAll(this.sheet);
        try {
          this.sheet.addRows(currentRowCount, additionalRows);
          
          // Synchronize data source with new row count
          this.syncDataSourceWithSheet();
        } finally {
          resumeAll(this.sheet);
        }
      }
      
      // Manually paste the data
      suspendAll(this.sheet);
      try {
        lines.forEach((line, index) => {
          const columns = line.split('\t');
          columns.forEach((value, colIndex) => {
            if (colIndex < 3) { // Only paste into available columns
              const targetRow = startRow + index;
              const targetCol = startCol + colIndex;
              
              // Clean the value (remove quotes if present)
              let cleanValue = value;
              if (cleanValue.startsWith('"') && cleanValue.endsWith('"')) {
                cleanValue = cleanValue.slice(1, -1);
              }
              
              this.sheet.setValue(targetRow, targetCol, cleanValue);
            }
          });
        });
      } finally {
        resumeAll(this.sheet);
      }
      
      // Trigger post-paste processing
      setTimeout(() => {
        try {
          this.addDataValidation();
          // Note: Empty rows are now included in initial data binding to prevent false change detection
          this.syncDataSourceWithSheet();
          
          // Scroll to show the pasted data
          if (rowCount > 1) {
            const lastPastedRow = startRow + rowCount - 1;
            this.sheet.showRow(lastPastedRow, GC.Spread.Sheets.VerticalPosition.center);
          }
        } catch (error) {
          console.error('Error in post-paste processing:', error);
        } finally {
          this.isProcessing = false;
        }
      }, 100);
      
    } catch (error) {
      console.error('Error in custom paste handler:', error);
      this.isProcessing = false;
      
      // Show error notification
      this.notifications.showToast(
        ToastLevel.Error,
        { message: 'Error pasting data. Please try again.' } as Toast
      );
    }
  }
}
